import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import RVCard from "@/components/inventory/RVCard";
import LeadForm from "@/components/LeadForm";
import { optimizedImg } from "@/lib/imageUtils";
import type { LandingPageContent } from "@/components/admin/LandingPageEditor";
import type { RVUnit } from "@/hooks/useInventory";
import { useThemeLayout } from "@/hooks/useThemeLayout";
import CarmaxPageHero from "@/components/carmax/CarmaxPageHero";
import LazySection from "@/components/ui/LazySection";
import { useInteractionReady } from "@/components/ui/InteractionGate";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

interface Props {
  content: LandingPageContent;
  pageSlug: string;
}

const INVENTORY_CARD_COLUMNS =
  "id,stock_number,title,slug,condition,status,rv_type,year,make,model," +
  "manufacturer,length_ft,weight_lbs,sleeps,price,sale_price,images," +
  "is_featured,is_special,special_label,created_at";

const overlayClass: Record<string, string> = {
  off: "bg-transparent",
  light: "bg-secondary/40",
  medium: "bg-secondary/60",
  strong: "bg-secondary/80",
};

async function trackEvent(pageSlug: string, event_type: string, unit_id?: string) {
  try {
    const sessionId =
      typeof window !== "undefined"
        ? window.sessionStorage.getItem("rex_session_id") ?? undefined
        : undefined;
    await supabase.from("landing_page_events").insert({
      page_slug: pageSlug,
      event_type,
      unit_id: unit_id ?? null,
      session_id: sessionId ?? null,
    });
  } catch {
    /* tracking failures must never break the UX */
  }
}

export default function LandingPageTemplate({ content, pageSlug }: Props) {
  const themeLayout = useThemeLayout();
  const engaged = useInteractionReady();
  const { hero, body_html, unit_ids = [], lead_form } = content;

  const { data: units = [] } = useQuery({
    queryKey: ["landing-units", pageSlug, unit_ids],
    enabled: engaged && unit_ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rv_inventory")
        .select(INVENTORY_CARD_COLUMNS)
        .in("id", unit_ids);
      if (error) throw error;
      const map = new Map(((data ?? []) as unknown as RVUnit[]).map((u) => [u.id, u]));
      return unit_ids.map((id) => map.get(id)).filter(Boolean) as RVUnit[];
    },
  });

  const align = hero.text_align === "left" ? "text-left items-start" : "text-center items-center";
  const overlay = overlayClass[hero.overlay ?? "medium"] ?? overlayClass['medium'];

  const heroBg = hero.image
    ? optimizedImg(hero.image, "hero", { q: 70 })
    : "";

  return (
    <article>
      {/* HERO */}
      {themeLayout === "carmax" ? (
        <CarmaxPageHero
          slides={[{
            image: hero.image || "",
            title: hero.headline,
            ...(hero.subheadline ? { subtitle: hero.subheadline } : {}),
            cta: hero.ctas && hero.ctas.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {hero.ctas.map((cta, i) => !cta.href ? null : (
                  <Button key={i} asChild size="lg" variant={cta.variant === "secondary" ? "outline" : "default"} onClick={() => trackEvent(pageSlug, "cta_click")}>
                    {cta.href.startsWith("http") || cta.href.startsWith("#") ? (
                      <a href={cta.href} target={cta.open_in_new_tab ? "_blank" : undefined} rel={cta.open_in_new_tab ? "noopener noreferrer" : undefined}>{cta.label}</a>
                    ) : <Link to={cta.href}>{cta.label}</Link>}
                  </Button>
                ))}
              </div>
            ) : undefined,
          }]}
        />
      ) : <section
        className="relative min-h-[60vh] md:min-h-[70vh] flex items-center justify-center overflow-hidden"
        style={
          heroBg
            ? { backgroundImage: `url(${heroBg})`, backgroundSize: "cover", backgroundPosition: "center" }
            : { background: "hsl(var(--secondary))" }
        }
      >
        {hero.image && <div className={`absolute inset-0 ${overlay}`} aria-hidden />}
        <div className={`relative container max-w-4xl py-16 md:py-24 flex flex-col ${align}`}>
          {hero.eyebrow && (
            <p className="text-xs md:text-sm uppercase tracking-widest text-primary-foreground/80 font-semibold mb-3">
              {hero.eyebrow}
            </p>
          )}
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-4">
            {hero.headline}
          </h1>
          {hero.subheadline && (
            <p className="text-base md:text-lg text-primary-foreground/90 max-w-2xl mb-6">
              {hero.subheadline}
            </p>
          )}
          {hero.ctas && hero.ctas.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-2">
              {hero.ctas.map((cta, i) => !cta.href ? null : (
                <Button
                  key={i}
                  asChild
                  size="lg"
                  variant={cta.variant === "secondary" ? "outline" : "default"}
                  onClick={() => trackEvent(pageSlug, "cta_click")}
                >
                  {cta.href.startsWith("http") || cta.href.startsWith("#") ? (
                    <a
                      href={cta.href}
                      target={cta.open_in_new_tab ? "_blank" : undefined}
                      rel={cta.open_in_new_tab ? "noopener noreferrer" : undefined}
                    >
                      {cta.label}
                    </a>
                  ) : (
                    <Link to={cta.href}>{cta.label}</Link>
                  )}
                </Button>
              ))}
            </div>
          )}
        </div>
      </section>}

      {/* BODY */}
      {body_html && (
        <section className="container max-w-3xl py-10 md:py-14">
          <div
            className="prose prose-neutral dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(body_html) }}
          />
        </section>
      )}

      {/* INVENTORY */}
      {units.length > 0 && (
        <LazySection as="section" className="container py-10 md:py-14" fallback={<div className="min-h-[400px]" />}>
          <div
            className="grid gap-4 sm:gap-5"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
          >
            {units.map((rv) => (
              <div key={rv.id} onClick={() => trackEvent(pageSlug, "card_click", rv.id)}>
                <RVCard rv={rv} />
              </div>
            ))}
          </div>
        </LazySection>
      )}

      {/* LEAD FORM */}
      {lead_form?.enabled && (
        <section id="lead-form" className="bg-muted/40 py-12 md:py-16"><LazySection fallback={<div className="min-h-[500px]" />}>
          <div className="container max-w-2xl">
            <div className="text-center mb-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
                {lead_form.heading || "Get Pricing & Availability"}
              </h2>
              {lead_form.subheading && (
                <p className="mt-2 text-sm md:text-base text-muted-foreground">
                  {lead_form.subheading}
                </p>
              )}
            </div>
            <LeadForm
              sourceDisplay={`Landing Page: ${pageSlug}`}
              showVehicleSection={false}
            />
          </div>
        </LazySection></section>
      )}
    </article>
  );
}